import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";

import { getSupportMetrics } from "@/services/supportChatService";

function formatMinutes(value: number | null): string {
  if (value === null) return "—";
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warn";
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={[
          "mt-1 text-2xl font-semibold",
          tone === "warn" ? "text-destructive" : "text-foreground",
        ].join(" ")}
      >
        {value}
      </p>
    </div>
  );
}

/**
 * Support health on the platform-metrics page.
 *
 * "Esperando respuesta" is the number that matters and is deliberately
 * NOT windowed — a thread from five weeks ago that nobody answered is
 * still unanswered, and hiding it behind a 30-day window is how it stays
 * that way. It renders in red the moment it is non-zero.
 */
export function SupportMetricsCard(): JSX.Element {
  const metrics = useQuery({
    queryKey: ["support-metrics"],
    queryFn: getSupportMetrics,
    refetchInterval: 60_000,
  });

  if (metrics.isLoading) {
    return (
      <div className="flex h-24 items-center justify-center rounded-lg border border-border bg-card">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const data = metrics.data;
  if (!data) return <></>;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-foreground">Soporte</h2>
        <span className="text-xs text-muted-foreground">
          últimos {data.windowDays} días
        </span>
      </div>

      {data.awaitingReply > 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {data.awaitingReply} conversación
            {data.awaitingReply === 1 ? "" : "es"} esperando respuesta
            {data.longestWaitingMinutes !== null
              ? ` — la más antigua hace ${formatMinutes(data.longestWaitingMinutes)}`
              : ""}
            .
          </span>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Conversaciones abiertas" value={String(data.conversationsOpened)} />
        <Stat
          label="Esperando respuesta"
          value={String(data.awaitingReply)}
          tone={data.awaitingReply > 0 ? "warn" : undefined}
        />
        <Stat
          label="Primera respuesta (mediana)"
          value={formatMinutes(data.medianFirstResponseMinutes)}
        />
        <Stat
          label="Fuera de SLA"
          value={String(data.slaBreaches)}
          tone={data.slaBreaches > 0 ? "warn" : undefined}
        />
      </div>
    </section>
  );
}
